import React, { FC, useCallback } from 'react'
import { useFormikContext } from 'formik'

import { UnderlineTitleProps } from '../UnderlineTitle'
import { FormFieldInputProps } from '../form/fields/FormFieldInput'
import { FormFieldSelectProps } from '../form/fields/FormFieldSelect'
import { ActionButtonProps } from '../ActionButton'
import FormikField from '../form/FormikField'
import { DeclarationItemType } from '../../graphql/generated/graphql'
import { FormFieldInputNumberProps } from '../form/fields/FormFieldInputNumber'

import * as SC from './styled'

type DeclarationEntryValues = {
  type: string
  companyJob: string
  siren?: string
  companyName: string
  entity: string
}

export type DeclarationEntryProps = {
  className?: string
  title: UnderlineTitleProps
  fields: {
    type: FormFieldSelectProps
    companyJob: FormFieldInputProps
    siren: FormFieldInputProps
    companyName?: FormFieldInputProps
    entity?: FormFieldInputProps
    postalCode?: FormFieldInputNumberProps
    address?: FormFieldInputProps
    city?: FormFieldInputProps
  }
  submitButton: ActionButtonProps
  cancelButton: ActionButtonProps
  closeButton?: ActionButtonProps
  isInEditMode?: boolean
  shouldResetForm?: boolean
}

const DeclarationEntry: FC<DeclarationEntryProps> = (props) => {
  const {
    className,
    title,
    fields,
    submitButton,
    cancelButton,
    closeButton,
    isInEditMode = false,
    shouldResetForm = true,
  } = props
  const { values, resetForm } = useFormikContext()
  const { type } = values as DeclarationEntryValues

  const handleSubmit = useCallback(() => {
    if (!submitButton?.onClick) return
    submitButton?.onClick(values as any)
    if (!shouldResetForm) return
    resetForm()
  }, [resetForm, shouldResetForm, submitButton, values])

  const handleCancel = useCallback(() => {
    if (!cancelButton?.onClick) return
    cancelButton?.onClick(values as any)
    resetForm()
  }, [cancelButton, resetForm, values])

  const handleClose = useCallback(() => {
    if (!closeButton?.onClick) return
    closeButton?.onClick(values as any)
    resetForm()
  }, [closeButton, resetForm, values])

  return (
    <SC.Container className={className} $isInEditMode={isInEditMode}>
      <SC.Title {...title} />
      <FormikField {...fields.type} Component={SC.Select} />
      <SC.Inputs>
        <FormikField {...fields.companyJob} Component={SC.Input} />
        {(type === DeclarationItemType.Participation ||
          type === DeclarationItemType.Function ||
          type === DeclarationItemType.Mandate) && (
          <FormikField {...fields.siren} Component={SC.Input} />
        )}
        {(type === DeclarationItemType.Participation || type === DeclarationItemType.Function) &&
          fields.companyName && (
            <FormikField
              {...fields.companyName}
              Component={SC.Input}
              disabled={
                type === DeclarationItemType.Participation || type === DeclarationItemType.Function
              }
            />
          )}
        {(type === DeclarationItemType.Mandate || type === DeclarationItemType.ForeignCompany) &&
          fields.entity && (
            <FormikField
              {...fields.entity}
              Component={SC.Input}
              disabled={type === DeclarationItemType.Mandate}
            />
          )}
        {fields.address && (
          <FormikField
            {...fields.address}
            Component={SC.Input}
            disabled={
              type === DeclarationItemType.Participation ||
              type === DeclarationItemType.Function ||
              type === DeclarationItemType.Mandate
            }
          />
        )}
        {fields.postalCode && (
          <FormikField
            {...fields.postalCode}
            Component={SC.Input}
            disabled={
              type === DeclarationItemType.Participation ||
              type === DeclarationItemType.Function ||
              type === DeclarationItemType.Mandate
            }
          />
        )}
        {fields.city && (
          <FormikField
            {...fields.city}
            Component={SC.Input}
            disabled={
              type === DeclarationItemType.Participation ||
              type === DeclarationItemType.Function ||
              type === DeclarationItemType.Mandate
            }
          />
        )}
      </SC.Inputs>
      <SC.Buttons>
        <SC.Button {...submitButton} onClick={handleSubmit} />
        <SC.Button {...cancelButton} onClick={handleCancel} />
        {closeButton && <SC.Button {...closeButton} onClick={handleClose} />}
      </SC.Buttons>
    </SC.Container>
  )
}

export default DeclarationEntry
